import React, { useState } from "react";
import { useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useDashAuth } from "../AuthContext";

export default function LoginPage() {
  const { user, loading, login } = useDashAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = location.state?.from || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!loading && user) {
    return <Navigate to={fromPath} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(fromPath, { replace: true });
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="dashboard-login-page">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.15),transparent_60%)] pointer-events-none" />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-blue-600 mb-3">
            <Lock className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-white">Portico Admin</h1>
          <p className="text-sm text-slate-400 mt-1">Sign in to manage submissions and leads.</p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white rounded-xl shadow-xl p-6 sm:p-8 space-y-4"
          data-testid="dashboard-login-form"
        >
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="dashboard-login-email-input"
              placeholder="you@vangelder-inc.com"
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="dashboard-login-password-input"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div
              role="alert"
              data-testid="dashboard-login-error"
              className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-md"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={submitting}
            data-testid="dashboard-login-submit-btn"
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Sign in
          </Button>

          <div className="text-center">
            <Link
              to="/dashboard/forgot-password"
              data-testid="dashboard-forgot-password-link"
              className="text-sm text-slate-600 hover:text-blue-700 hover:underline"
            >
              Forgot password?
            </Link>
          </div>

          <p className="text-xs text-slate-500 text-center">
            Trouble signing in? Contact your dashboard administrator.
          </p>
        </form>

        <p className="text-center text-xs text-slate-500 mt-4">
          Authorized personnel only. All access is logged.
        </p>
      </div>
    </div>
  );
}
